// Wall-clock text in a chosen zone, both ways.
//
// The admin panel needs numeric time entry because at seven-day zoom one pixel is minutes and
// dragging alone cannot reach second precision. `<input type="datetime-local">` is not usable
// for it: it is always in the browser's own zone, while this UI has a zone toggle, so a person
// viewing the archive in station time would type station time and get an instant hours away.
//
// So the conversion is done here. It is a two-pass fixed point, which is the standard way to
// invert a zone: an offset is a function of the instant, and the instant is what is being
// solved for.

const PARTS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
};

const cache = new Map<string, Intl.DateTimeFormat>();

function partFormatter(zone: string): Intl.DateTimeFormat {
  let found = cache.get(zone);
  if (!found) {
    found = new Intl.DateTimeFormat("en-US", { ...PARTS, timeZone: zone });
    cache.set(zone, found);
  }
  return found;
}

/** The instant's wall-clock fields in `zone`, re-encoded as if they were UTC. */
function wallClockAsUtc(ms: number, zone: string): number {
  const parts = partFormatter(zone).formatToParts(ms);
  const get = (type: Intl.DateTimeFormatPartTypes): number => {
    const part = parts.find((candidate) => candidate.type === type);
    return part ? Number(part.value) : 0;
  };
  // en-US with hour12:false renders midnight as hour 24 rather than 0 in some engines.
  const hour = get("hour") % 24;
  return Date.UTC(get("year"), get("month") - 1, get("day"), hour, get("minute"), get("second"));
}

/**
 * The zone's UTC offset in milliseconds at a given instant. Positive east of Greenwich.
 *
 * Computed from the instant truncated to a whole second. `Intl` only renders down to
 * seconds, so subtracting an untruncated instant from a truncated rendering would fold the
 * milliseconds into the offset -- an offset of -337 ms for UTC, which is not an offset at
 * all. No zone has ever had a sub-second offset, so truncating loses nothing.
 */
export function offsetMsAt(ms: number, zone: string): number {
  const whole = Math.floor(ms / 1000) * 1000;
  return wallClockAsUtc(whole, zone) - whole;
}

const PATTERN = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{1,2}):(\d{2})(?::(\d{2}))?$/;

/**
 * Read `YYYY-MM-DD HH:MM[:SS]` as an instant in `zone`.
 *
 * Two passes. The first uses the offset in force at the naive guess, which is wrong only
 * when the guess and the answer straddle a daylight-saving change; the second uses the offset
 * at the first pass's answer, which resolves it. A third pass would never change anything,
 * because offsets do not change twice within one offset's distance.
 *
 * Returns null rather than NaN for anything unparseable, so a half-typed field reads as
 * "not yet valid" instead of silently becoming an invalid date.
 */
export function zonedToMs(text: string, zone: string): number | null {
  const match = PATTERN.exec(text.trim());
  if (!match) return null;

  const [, year, month, day, hour, minute, second] = match;
  const naive = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second ?? "0"),
  );
  if (!Number.isFinite(naive)) return null;

  const firstPass = naive - offsetMsAt(naive, zone);
  const result = naive - offsetMsAt(firstPass, zone);
  return Number.isFinite(result) ? result : null;
}

/** Render an instant as `YYYY-MM-DD HH:MM:SS` in `zone`, ready to be edited and read back. */
export function msToZonedText(ms: number, zone: string): string {
  const asUtc = new Date(wallClockAsUtc(ms, zone));
  const pad = (value: number, width = 2) => String(value).padStart(width, "0");
  return (
    `${pad(asUtc.getUTCFullYear(), 4)}-${pad(asUtc.getUTCMonth() + 1)}-${pad(asUtc.getUTCDate())}` +
    ` ${pad(asUtc.getUTCHours())}:${pad(asUtc.getUTCMinutes())}:${pad(asUtc.getUTCSeconds())}`
  );
}
